import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import './FilterBar.css';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'number';
  placeholder?: string;
  options?: FilterOption[];
  defaultValue?: string | number | number[];
  isMulti?: boolean;
  onChange?: (value: any) => void;
}

interface FilterBarProps {
  title?: string;
  fields: FilterField[];
  onFilter: (filters: Record<string, any>) => void;
  onReset?: () => void;
  showAddButton?: boolean;
  addButtonLabel?: string;
  onAddClick?: () => void;
  addButtonVariant?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  title = "Filters",
  fields,
  onFilter,
  onReset,
  showAddButton = false,
  addButtonLabel = "Add New",
  onAddClick,
  addButtonVariant = "primary"
}) => {
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    fields.forEach(field => {
      initialFilters[field.name] = field.defaultValue || '';
    });
    return initialFilters;
  });
  
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Call onChange if provided
    const field = fields.find(f => f.name === name);
    if (field?.onChange) {
      field.onChange(value);
    }
  };

  const handleApplyFilters = () => {
    onFilter(filters);
  };

  const handleResetFilters = () => {
    const resetFilters: Record<string, any> = {};
    fields.forEach(field => {
      resetFilters[field.name] = field.defaultValue || '';
    });
    setFilters(resetFilters);
    setOpenDropdowns({});
    onFilter(resetFilters);
    if (onReset) {
      onReset();
    }
  };

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (fieldName: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const renderFilterField = (field: FilterField) => {
    const value = filters[field.name] || '';

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <Form.Control
            type={field.type}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => handleFilterChange(field.name, e.target.value)}
            size="sm"
          />
        );

      case 'select':
        if (field.isMulti) {
          return (
            <div className={`dropdown-container ${openDropdowns[field.name] ? 'open' : ''}`} ref={el => dropdownRefs.current[field.name] = el}>
              <button
                className="custom-select-button"
                type="button"
                onClick={() => toggleDropdown(field.name)}
              >
                <span className="button-text">
                  {Array.isArray(value) && value.length > 0 
                    ? `${value.length} selected` 
                    : 'Select options...'}
                </span>
              </button>
              <div className={`dropdown-menu ${openDropdowns[field.name] ? 'show' : ''}`}>
                {field.options?.map((option) => (
                  <label key={option.value} className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={Array.isArray(value) && value.includes(parseInt(option.value))}
                      onChange={(e) => {
                        const currentValue = Array.isArray(value) ? [...value] : [];
                        if (e.target.checked) {
                          if (!currentValue.includes(parseInt(option.value))) {
                            currentValue.push(parseInt(option.value));
                          }
                        } else {
                          const index = currentValue.indexOf(parseInt(option.value));
                          if (index > -1) {
                            currentValue.splice(index, 1);
                          }
                        }
                        handleFilterChange(field.name, currentValue);
                      }}
                      className="me-2"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          );
        }
        return (
          <Form.Select
            value={value}
            onChange={(e) => handleFilterChange(field.name, e.target.value)}
            size="sm"
          >
            <option value="">All</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );

      case 'date':
        return (
          <Form.Control
            type="date"
            value={value}
            onChange={(e) => handleFilterChange(field.name, e.target.value)}
            size="sm"
          />
        );

      default:
        return null;
    }
  };

  return (
    <Card className="filter-bar mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 text-muted">{title}</h6>
          {showAddButton && (
            <Button 
              variant={addButtonVariant} 
              size="sm" 
              onClick={onAddClick}
              className="add-button"
            >
              <i className="fas fa-plus me-1"></i>
              {addButtonLabel}
            </Button>
          )}
        </div>

        <Row className="g-2">
          {fields.map((field) => (
            <Col md={3} sm={6} key={field.name}>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">
                  {field.label}
                </Form.Label>
                {renderFilterField(field)}
              </Form.Group>
            </Col>
          ))}
          
          <Col md={3} sm={6} className="d-flex align-items-end">
            <div className="filter-actions w-100">
              <Button 
                variant="success" 
                size="sm" 
                onClick={handleApplyFilters}
                className="me-2 filter-button"
              >
                <i className="fas fa-search me-1"></i>
                Apply
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={handleResetFilters}
                className="filter-button"
              >
                <i className="fas fa-redo me-1"></i>
                Reset
              </Button>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default FilterBar; 